// My List: the rules the page and its tests share.
import { isMyListReminderDue } from './navCounts.js';

export { isMyListReminderDue };

export const PRIORITIES = [
  { id: 'high',   label: 'High',   pill: 'bg-red-50 text-red-700',    edge: 'border-l-red-500' },
  { id: 'normal', label: 'Normal', pill: 'bg-blue-50 text-blue-700',  edge: 'border-l-blue-400' },
  { id: 'low',    label: 'Low',    pill: 'bg-gray-100 text-gray-600', edge: 'border-l-gray-300' },
];
export const priorityMeta = (p) => PRIORITIES.find(x => x.id === p) || PRIORITIES[1];
const RANK = { high: 0, normal: 1, low: 2 };

/**
 * Open notes: pinned first, then by priority, then a reminder that has come
 * round, then the newest. Done notes: most recently ticked off first.
 */
export function sortNotes(notes, now = new Date()) {
  return [...(notes || [])].sort((a, b) => {
    if (a.done_at || b.done_at) return String(b.done_at || '').localeCompare(String(a.done_at || ''));
    if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
    const pr = (RANK[a.priority] ?? 1) - (RANK[b.priority] ?? 1);
    if (pr) return pr;
    const da = isMyListReminderDue(a, now), db = isMyListReminderDue(b, now);
    if (da !== db) return da ? -1 : 1;
    return String(b.created_at || '').localeCompare(String(a.created_at || ''));
  });
}

const pad = (n) => String(n).padStart(2, '0');

/** A stored UTC ISO reminder as the value a datetime-local input shows, in the browser's time. */
export function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** What a datetime-local input says, in the browser's time, as the UTC ISO the API stores. */
export function fromLocalInput(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** The local date (YYYY-MM-DD) of a reminder, for an Action's due date. */
export const localDateOf = (iso) => toLocalInput(iso).slice(0, 10);

/**
 * Quick reminder choices, each a datetime-local value. "Later today" is only
 * offered while there is a later today to have (before 16:00).
 */
export function reminderPresets(now = new Date()) {
  const at = (days, hour) => {
    const d = new Date(now);
    d.setDate(d.getDate() + days);
    d.setHours(hour, 0, 0, 0);
    return toLocalInput(d.toISOString());
  };
  const out = [];
  if (now.getHours() < 16) out.push({ label: 'Later today', value: at(0, 17) });
  out.push({ label: 'Tomorrow morning', value: at(1, 9) });
  const toMonday = ((8 - now.getDay()) % 7) || 7;
  out.push({ label: 'Next Monday', value: at(toMonday, 9) });
  out.push({ label: 'In a week', value: at(7, 9) });
  return out;
}

/** "Tue 4 Nov, 09:00", with the year only when it is not this year. */
export function reminderLabel(iso, now = new Date()) {
  if (!iso) return '';
  const d = new Date(iso);
  const opts = { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' };
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return d.toLocaleString('en-GB', opts);
}

/** Free-text search over a note's title and text. */
export function matchesSearch(note, q) {
  const s = (q || '').trim().toLowerCase();
  if (!s) return true;
  return `${note.title || ''}\n${note.body || ''}`.toLowerCase().includes(s);
}
